/**
 * NoteService - Manages notes within a vault
 *
 * Notes are stored as a subcollection under their vault:
 *   knowledgebase/{vaultId}/notes/{noteId}
 *
 * This service requires a vaultId for all operations.
 */

import {
  collection,
  query,
  getDocs,
  where,
  limit,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  orderBy,
  writeBatch
} from 'firebase/firestore'
import { collectionGroup } from 'firebase/firestore'
import { onAuthStateChanged } from 'firebase/auth'
import { db, auth } from '@/services/firebase/config'
import { parseMarkdown, slugify, normalizeFrontmatter } from './LinkParser.js'
import { initDatabase, saveToDb, getFromDb } from '@/utils/indexedDBHelper'

const COLLECTION = 'knowledgebase'
const NOTES_SUBCOLLECTION = 'notes'

const DB_NAME = 'kb-notes-db'
const DB_VERSION = 1
const STORE_NAME = 'notes'

const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)))
const NOTE_PERMISSIONS = Object.freeze({
  PUBLIC: 'PUBLIC',
  CAMPAIGN: 'CAMPAIGN',
  USER_SPECIFIC: 'USER_SPECIFIC'
})

function isPermissionDeniedError(error) {
  const code = String(error?.code || '').toLowerCase()
  const message = String(error?.message || '').toLowerCase()
  return (
    code.includes('permission-denied') ||
    code.includes('permission') ||
    message.includes('missing or insufficient permissions') ||
    message.includes('permission')
  )
}

function sortNotesByTitle(notes = []) {
  return [...notes].sort((left, right) =>
    String(left?.title || '').localeCompare(String(right?.title || ''), undefined, {
      sensitivity: 'base'
    })
  )
}

// Lightweight in-memory cache for shareable note lookups to avoid repeat reads in-session
const _shareLookupCache = new Map() // key: ownerId|slug|viewerUid, value: { ts, note }
const SHARE_CACHE_TTL_MS = 2 * 60 * 1000 // 2 minutes

// Invalidate session cache on auth changes (logout/login/switch user)
let _lastAuthUid = null
try {
  _lastAuthUid = auth?.currentUser?.uid || null
  if (auth && typeof onAuthStateChanged === 'function') {
    onAuthStateChanged(auth, (u) => {
      const uid = u?.uid || null
      if (uid !== _lastAuthUid) {
        try {
          _shareLookupCache.clear()
        } catch {}
        _lastAuthUid = uid
      }
    })
  }
} catch {}

/**
 * Flatten array - handles cases where arrays might accidentally become nested
 */
const flattenArray = (arr) => {
  if (!Array.isArray(arr)) return []
  return arr.flat(Infinity).filter((item) => typeof item === 'string' && item.trim())
}

/**
 * Recursively sanitize an object to prevent nested arrays in Firestore
 * Firestore doesn't support arrays containing arrays
 */
const sanitizeForFirestore = (obj, depth = 0) => {
  // Prevent infinite recursion
  if (depth > 10) return obj

  if (obj === null || obj === undefined) return obj
  if (typeof obj !== 'object') return obj

  // Handle special Firestore types (like serverTimestamp)
  if (obj.constructor && obj.constructor.name === 'FieldValue') {
    return obj
  }

  if (Array.isArray(obj)) {
    // Check if array contains any arrays (nested arrays - not allowed in Firestore)
    const hasNestedArray = obj.some((item) => Array.isArray(item))
    if (hasNestedArray) {
      // Try to flatten to primitives only
      const flattened = obj.flat(Infinity)
      // Keep only primitives (strings, numbers, booleans)
      return flattened.filter(
        (item) =>
          item !== null &&
          item !== undefined &&
          (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')
      )
    }
    // Recursively sanitize each item in the array
    return obj
      .map((item) => sanitizeForFirestore(item, depth + 1))
      .filter((item) => item !== undefined)
  }

  // It's an object - recursively sanitize each property
  const result = {}
  for (const [key, value] of Object.entries(obj)) {
    const sanitized = sanitizeForFirestore(value, depth + 1)
    // Skip undefined values
    if (sanitized !== undefined) {
      result[key] = sanitized
    }
  }
  return result
}

/**
 * Permission levels for notes
 */
export const PERMISSIONS = {
  PRIVATE: 'PRIVATE',
  CAMPAIGN: 'CAMPAIGN',
  PUBLIC: 'PUBLIC',
  USER_SPECIFIC: 'USER_SPECIFIC'
}

const normalizePermission = (value, fallback = PERMISSIONS.PRIVATE) => {
  const perm = String(value || '').toUpperCase()
  return PERMISSIONS[perm] || fallback
}

/**
 * Get the notes collection reference for a vault
 */
function getNotesCollection(vaultId) {
  return collection(db, COLLECTION, vaultId, NOTES_SUBCOLLECTION)
}

/**
 * Get a note document reference
 */
function getNoteDoc(vaultId, noteId) {
  return doc(db, COLLECTION, vaultId, NOTES_SUBCOLLECTION, noteId)
}

export class NoteService {
  /**
   * Initialize IndexedDB for caching
   */
  static async initDb(stateRef) {
    return initDatabase(DB_NAME, DB_VERSION, STORE_NAME, stateRef)
  }

  /**
   * Cache key for a vault's notes
   */
  static cacheKey(vaultId) {
    return `vault_notes_${vaultId}`
  }

  /**
   * Get cached notes for a vault
   */
  static async getCachedNotes(vaultId) {
    try {
      return await getFromDb(DB_NAME, STORE_NAME, NoteService.cacheKey(vaultId))
    } catch {
      return null
    }
  }

  /**
   * Cache notes for a vault
   */
  static async cacheNotes(vaultId, notes) {
    const toIso = (ts) => {
      try {
        if (!ts) return null
        if (typeof ts === 'string') return ts
        if (ts instanceof Date) return ts.toISOString()
        if (typeof ts.toDate === 'function') return ts.toDate().toISOString()
        if (typeof ts._seconds === 'number') return new Date(ts._seconds * 1000).toISOString()
      } catch {
        /* ignore parse errors */
      }
      return null
    }

    try {
      const safe = (notes || []).map((n) => ({
        ...n,
        createdAt: toIso(n?.createdAt),
        updatedAt: toIso(n?.updatedAt)
      }))
      await saveToDb(
        DB_NAME,
        STORE_NAME,
        NoteService.cacheKey(vaultId),
        'collection',
        vaultId,
        safe
      )
    } catch {
      // Ignore cache errors
    }
  }

  /**
   * Normalize note from Firestore document
   */
  static normalizeFromDoc(docSnap, vaultId = null) {
    const data = docSnap?.data ? docSnap.data() : docSnap
    if (!data) return null
    const id = docSnap?.id || data.id

    return {
      id,
      vaultId: vaultId || data.vaultId || null,
      ownerId: data.ownerId || null,
      title: data.title || 'Untitled',
      slug: data.slug || slugify(data.title || id || 'note'),
      content: data.content || '',
      folder: data.folder || '',
      icon: data.icon || null,
      frontmatter: data.frontmatter || {},
      tags: Array.isArray(data.tags) ? data.tags : [],
      aliases: Array.isArray(data.aliases) ? data.aliases : [],
      links: Array.isArray(data.links) ? data.links : [],
      permissions: normalizePermission(
        data.permissions || data.frontmatter?.permissions,
        PERMISSIONS.PRIVATE
      ),
      createdAt: data.createdAt || null,
      updatedAt: data.updatedAt || null,
      reportsCount: data.reportsCount || 0
    }
  }

  /**
   * Build payload for creating/updating a note
   */
  static buildPayload(draft = {}, { isNew = false } = {}) {
    const rawContent = String(draft.content || '')
    const parsed = parseMarkdown(rawContent)
    const mergedFrontmatter = normalizeFrontmatter({
      ...(draft.frontmatter || {}),
      ...(parsed.frontmatter || {})
    })

    // Flatten and dedupe tags/aliases to prevent nested array errors in Firestore
    const draftTags = flattenArray(draft.tags)
    const parsedTags = flattenArray(parsed.tags)
    const draftAliases = flattenArray(draft.aliases)
    const parsedAliases = flattenArray(parsed.aliases)

    const tags = uniq([...draftTags, ...parsedTags])
    const aliases = uniq([...draftAliases, ...parsedAliases])
    const title = String(draft.title || 'Untitled').trim()
    const slug = draft.slug ? slugify(String(draft.slug)) : slugify(title) || slugify('note')

    const permissions = normalizePermission(
      mergedFrontmatter.permissions || draft.permissions,
      PERMISSIONS.PRIVATE
    )

    // Also flatten frontmatter tags/aliases to be safe
    const safeFrontmatter = {
      ...mergedFrontmatter,
      tags: flattenArray(mergedFrontmatter.tags).length
        ? flattenArray(mergedFrontmatter.tags)
        : tags,
      aliases: flattenArray(mergedFrontmatter.aliases).length
        ? flattenArray(mergedFrontmatter.aliases)
        : aliases,
      permissions,
      campaignId: draft.campaignId || mergedFrontmatter.campaignId || null,
      campaignIds: Array.isArray(draft.campaignIds)
        ? Array.from(new Set(draft.campaignIds.filter(Boolean)))
        : Array.isArray(mergedFrontmatter.campaignIds)
          ? Array.from(new Set(mergedFrontmatter.campaignIds.filter(Boolean)))
          : [],
      allowedUserIds: Array.isArray(draft.allowedUserIds)
        ? Array.from(new Set(draft.allowedUserIds.filter(Boolean)))
        : Array.isArray(mergedFrontmatter.allowedUserIds)
          ? Array.from(new Set(mergedFrontmatter.allowedUserIds.filter(Boolean)))
          : []
    }

    const payload = {
      title,
      slug,
      content: parsed.body || '',
      folder: draft.folder || '',
      icon: draft.icon || null,
      frontmatter: safeFrontmatter,
      tags,
      aliases,
      links: parsed.links || [],
      permissions,
      campaignId:
        safeFrontmatter.campaignId ||
        (safeFrontmatter.campaignIds.length > 0 ? safeFrontmatter.campaignIds[0] : null),
      campaignIds: safeFrontmatter.campaignIds || [],
      allowedUserIds: safeFrontmatter.allowedUserIds || []
    }

    if (isNew) {
      payload.createdAt = serverTimestamp()
      // Include ownerId for new notes (required for permission checks)
      if (draft.ownerId) {
        payload.ownerId = draft.ownerId
      }
    }
    payload.updatedAt = serverTimestamp()

    // Sanitize entire payload to prevent nested arrays
    // We save serverTimestamp values first since they're special Firestore types
    const createdAtValue = payload.createdAt
    const updatedAtValue = payload.updatedAt

    const sanitized = sanitizeForFirestore(payload)

    // Restore serverTimestamp values
    if (createdAtValue) sanitized.createdAt = createdAtValue
    sanitized.updatedAt = updatedAtValue

    return sanitized
  }

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * List all notes in a vault
   * @param {string} vaultId - Vault ID
   * @returns {Promise<Array>} Array of notes
   */
  static async listNotes(vaultId) {
    if (!vaultId) return []

    const notesRef = getNotesCollection(vaultId)
    try {
      const q = query(notesRef, orderBy('title', 'asc'))
      const snapshot = await getDocs(q)
      return snapshot.docs.map((docSnap) => NoteService.normalizeFromDoc(docSnap, vaultId))
    } catch (error) {
      const viewerUid = auth.currentUser?.uid || null
      if (!isPermissionDeniedError(error)) throw error

      const fallbackQueries = [query(notesRef, where('permissions', '==', NOTE_PERMISSIONS.PUBLIC))]
      if (viewerUid) {
        fallbackQueries.push(query(notesRef, where('permissions', '==', NOTE_PERMISSIONS.CAMPAIGN)))
        fallbackQueries.push(query(notesRef, where('allowedUserIds', 'array-contains', viewerUid)))
        fallbackQueries.push(query(notesRef, where('allowedUserIds', 'array-contains', '__all_players__')))
      }

      const settled = await Promise.all(
        fallbackQueries.map((queryRef) =>
          getDocs(queryRef)
            .then((snapshot) => ({ ok: true, snapshot }))
            .catch((queryError) => ({ ok: false, error: queryError }))
        )
      )

      const successfulSnapshots = settled.filter((entry) => entry.ok).map((entry) => entry.snapshot)
      if (!successfulSnapshots.length) throw error

      const notesById = new Map()
      for (const snapshot of successfulSnapshots) {
        for (const docSnap of snapshot.docs) {
          const note = NoteService.normalizeFromDoc(docSnap, vaultId)
          if (note?.id) notesById.set(note.id, note)
        }
      }

      return sortNotesByTitle(Array.from(notesById.values()))
    }
  }

  /**
   * List only PUBLIC notes in a vault (for public/shared views)
   * Satisfies query-based rules by constraining to permissions == 'PUBLIC'
   */
  static async listPublicNotes(vaultId) {
    if (!vaultId) return []
    const notesRef = getNotesCollection(vaultId)
    const qy = query(notesRef, where('permissions', '==', 'PUBLIC'), orderBy('title', 'asc'))
    const snapshot = await getDocs(qy)
    return snapshot.docs.map((docSnap) => NoteService.normalizeFromDoc(docSnap, vaultId))
  }

  /**
   * Get a single note
   * @param {string} vaultId - Vault ID
   * @param {string} noteId - Note ID
   * @returns {Promise<Object|null>} Note or null
   */
  static async getNote(vaultId, noteId) {
    if (!vaultId || !noteId) return null

    const docRef = getNoteDoc(vaultId, noteId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) return null
    return NoteService.normalizeFromDoc(docSnap, vaultId)
  }

  /**
   * Create a new note
   * @param {string} vaultId - Vault ID
   * @param {Object} draft - Note data
   * @returns {Promise<Object>} Created note with ID
   */
  static async createNote(vaultId, draft) {
    if (!vaultId) throw new Error('vaultId required')

    // buildPayload already sanitizes and adds serverTimestamp
    const payload = NoteService.buildPayload(draft, { isNew: true })

    const notesRef = getNotesCollection(vaultId)
    const docRef = await addDoc(notesRef, payload)

    return {
      id: docRef.id,
      vaultId,
      ...payload,
      createdAt: new Date(),
      updatedAt: new Date()
    }
  }

  /**
   * Create a linked note only if it doesn't already exist in the same folder.
   * Uses best-effort queries (no transaction) to avoid Firestore query limitations.
   * @param {string} vaultId
   * @param {Object} draft
   * @returns {Promise<{ note: Object|null, created: boolean }>}
   */
  static async createLinkedNoteIfMissing(vaultId, draft) {
    if (!vaultId) throw new Error('vaultId required')
    const normalizedSlug = slugify(draft?.slug || draft?.title || 'note')
    const targetFolder = draft?.folder || ''
    const targetTitle = String(draft?.title || '').trim()

    const notesRef = getNotesCollection(vaultId)
    const slugQuery = query(notesRef, where('slug', '==', normalizedSlug), limit(25))
    const titleQuery =
      targetTitle.length > 0 ? query(notesRef, where('title', '==', targetTitle), limit(25)) : null

    try {
      const slugSnap = await getDocs(slugQuery)
      if (!slugSnap.empty) {
        for (const docSnap of slugSnap.docs) {
          const data = docSnap.data()
          if ((data?.folder || '') === targetFolder) {
            const existing = NoteService.normalizeFromDoc(docSnap, vaultId)
            return { note: existing, created: false }
          }
        }
      }

      if (titleQuery) {
        const titleSnap = await getDocs(titleQuery)
        if (!titleSnap.empty) {
          for (const docSnap of titleSnap.docs) {
            const data = docSnap.data()
            if ((data?.folder || '') === targetFolder) {
              const existing = NoteService.normalizeFromDoc(docSnap, vaultId)
              return { note: existing, created: false }
            }
          }
        }
      }
    } catch {
      // If lookup fails, fall through and attempt creation
    }

    const payload = NoteService.buildPayload(
      { ...draft, slug: normalizedSlug, folder: targetFolder },
      { isNew: true }
    )
    const docRef = await addDoc(notesRef, payload)

    return {
      note: {
        id: docRef.id,
        vaultId,
        ...payload,
        createdAt: new Date(),
        updatedAt: new Date()
      },
      created: true
    }
  }

  /**
   * Create multiple notes in batches (no existence checks).
   * Intended for bulk wikilink stub creation after local dedupe.
   * @param {string} vaultId
   * @param {Array<Object>} drafts
   * @returns {Promise<Array<Object>>}
   */
  static async createNotesBatch(vaultId, drafts = []) {
    if (!vaultId || !Array.isArray(drafts) || drafts.length === 0) return []

    const notesRef = getNotesCollection(vaultId)
    const created = []
    const chunkSize = 450

    for (let i = 0; i < drafts.length; i += chunkSize) {
      const batch = writeBatch(db)
      const slice = drafts.slice(i, i + chunkSize)
      for (const draft of slice) {
        const payload = NoteService.buildPayload(draft, { isNew: true })
        const docRef = doc(notesRef)
        batch.set(docRef, payload)
        created.push({
          id: docRef.id,
          vaultId,
          ...payload,
          createdAt: new Date(),
          updatedAt: new Date()
        })
      }
      await batch.commit()
    }

    return created
  }

  /**
   * Update a note
   * @param {string} vaultId - Vault ID
   * @param {string} noteId - Note ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated note data
   */
  static async updateNote(vaultId, noteId, updates) {
    if (!vaultId || !noteId) throw new Error('vaultId and noteId required')

    const docRef = getNoteDoc(vaultId, noteId)
    const existingSnap = await getDoc(docRef)
    if (!existingSnap.exists()) throw new Error('note not found')
    const existing = existingSnap.data() || {}

    // For partial updates (icon, folder), don't rebuild full payload
    const keys = Object.keys(updates || {})
    // Include permissions to ensure slug is generated/consistent when sharing
    const requiresRebuild = keys.some((k) =>
      ['content', 'title', 'slug', 'permissions'].includes(k)
    )
    const isPartialUpdate = !requiresRebuild

    let payload
    if (isPartialUpdate) {
      // For partial updates, sanitize and add timestamp
      payload = sanitizeForFirestore({ ...updates })
      // If existing slug is missing or placeholder, backfill from title (existing or incoming)
      try {
        const needSlug = !existing.slug || String(existing.slug).toLowerCase() === 'untitled'
        const candidateTitle = updates.title ?? existing.title ?? 'Untitled'
        if (needSlug && candidateTitle) {
          payload.slug = slugify(candidateTitle)
        }
        // Backfill ownerId if it's missing on the document and we have an owner/ownerId source
        if (!existing.ownerId && (existing.owner || updates.ownerId)) {
          payload.ownerId = updates.ownerId || existing.owner || null
        }
      } catch {}
      payload.updatedAt = serverTimestamp()
    } else {
      // Merge existing fields to avoid overwriting unspecified fields like title/folder/content
      const mergedDraft = { ...existing, ...updates }
      // buildPayload already sanitizes and adds serverTimestamp
      payload = NoteService.buildPayload(mergedDraft, { isNew: false })
      // If title is being updated, ensure slug follows new title unless caller explicitly provided slug
      if (Object.prototype.hasOwnProperty.call(updates, 'title')) {
        try {
          const nextTitle = String(updates.title || '').trim()
          if (nextTitle) payload.slug = slugify(nextTitle)
        } catch {}
      }
    }

    await updateDoc(docRef, payload)

    // Return the full payload (includes parsed links) so store can update properly
    return {
      id: noteId,
      vaultId,
      ...payload,
      updatedAt: new Date()
    }
  }

  /**
   * Delete a note
   * @param {string} vaultId - Vault ID
   * @param {string} noteId - Note ID
   * @returns {Promise<boolean>} Success
   */
  static async deleteNote(vaultId, noteId) {
    if (!vaultId || !noteId) throw new Error('vaultId and noteId required')

    await deleteDoc(getNoteDoc(vaultId, noteId))
    return true
  }

  /**
   * Find a PUBLIC note by ownerId and slug across all vaults using collectionGroup
   * @param {string} ownerId
   * @param {string} slug
   * @returns {Promise<Object|null>} Normalized note or null
   */
  static async findPublicNoteByOwnerAndSlug(ownerId, slug) {
    if (!ownerId || !slug) return null
    try {
      // Wait briefly for auth to initialize to avoid transient anonymous vs authed races
      try {
        if (!auth?.currentUser) {
          await new Promise((resolve) => {
            let done = false
            const unsub =
              typeof onAuthStateChanged === 'function'
                ? onAuthStateChanged(auth, () => {
                    if (!done) {
                      done = true
                      try {
                        if (typeof unsub === 'function') unsub()
                      } catch {}
                      resolve(true)
                    }
                  })
                : null
            setTimeout(() => {
              if (!done) {
                done = true
                try {
                  if (typeof unsub === 'function') unsub()
                } catch {}
                resolve(false)
              }
            }, 1200)
          })
        }
      } catch {}
      const cg = collectionGroup(db, NOTES_SUBCOLLECTION)

      // Single-query path:
      // - Anonymous: PUBLIC only
      // - Authenticated: PUBLIC | CAMPAIGN | USER_SPECIFIC (rules still enforce access)
      const viewerUid = auth.currentUser?.uid || null
      let perms = viewerUid ? ['PUBLIC', 'CAMPAIGN', 'USER_SPECIFIC'] : ['PUBLIC']

      // Cache check
      const cacheKey = `${ownerId}|${slug}|${viewerUid || 'anon'}`
      const now = Date.now()
      const cached = _shareLookupCache.get(cacheKey)
      if (cached && now - cached.ts < SHARE_CACHE_TTL_MS) {
        return cached.note
      }

      // Owner + slug + relevant permissions
      async function runQuery(p) {
        const qy = query(
          cg,
          where('ownerId', '==', ownerId),
          where('slug', '==', slug),
          where('permissions', 'in', p),
          limit(1)
        )
        return getDocs(qy)
      }
      let snap
      try {
        snap = await runQuery(perms)
      } catch (err) {
        // If rules or indexes temporarily reject mixed-permission queries during auth race, retry PUBLIC-only
        if (
          String(err?.code || '').includes('permission') ||
          String(err?.message || '')
            .toLowerCase()
            .includes('permission')
        ) {
          try {
            snap = await runQuery(['PUBLIC'])
          } catch (e2) {
            throw err
          }
        } else {
          throw err
        }
      }
      if (snap.empty) return null

      const winner = snap.docs[0]
      // If owner is viewing and slug is placeholder/missing, backfill from title
      try {
        const currentUid = auth.currentUser?.uid
        const wdata = winner?.data?.() || winner.data?.() || winner.data()
        const desiredSlug = slugify((wdata && wdata.title) || '')
        if (
          currentUid &&
          currentUid === ownerId &&
          wdata &&
          (!wdata.slug || wdata.slug === 'untitled') &&
          desiredSlug
        ) {
          await updateDoc(winner.ref, { slug: desiredSlug })
        }
      } catch {}
      const vaultId = winner.ref?.parent?.parent?.id || null
      const note = NoteService.normalizeFromDoc(winner, vaultId)
      _shareLookupCache.set(cacheKey, { ts: now, note })
      return note
    } catch (e) {
      if (process.env && process.env.NODE_ENV !== 'production') {
        console.warn(
          '[NoteService] findPublicNoteByOwnerAndSlug failed. Code:',
          e.code,
          'Message:',
          e.message
        )
        if (e.code === 'failed-precondition' && e.message.includes('index')) {
          console.error(
            'MISSING INDEX: Please check the browser console for the direct link to create the missing Firestore index, or ensure firestore.indexes.json is deployed.'
          )
        }
      }
      throw e
    }
  }

  /**
   * Move notes to a different folder
   * @param {string} vaultId - Vault ID
   * @param {Array<string>} noteIds - Note IDs to move
   * @param {string} targetFolder - Target folder path
   */
  static async moveNotes(vaultId, noteIds, targetFolder) {
    if (!vaultId || !noteIds?.length) return

    const batch = writeBatch(db)

    for (const noteId of noteIds) {
      const docRef = getNoteDoc(vaultId, noteId)
      batch.update(docRef, {
        folder: targetFolder || '',
        updatedAt: serverTimestamp()
      })
    }

    await batch.commit()
  }

  /**
   * Rename a folder (update all notes in that folder)
   * @param {string} vaultId - Vault ID
   * @param {string} oldPath - Old folder path
   * @param {string} newPath - New folder path
   */
  static async renameFolder(vaultId, oldPath, newPath) {
    if (!vaultId || !oldPath || oldPath === newPath) return

    const notes = await NoteService.listNotes(vaultId)
    const batch = writeBatch(db)

    for (const note of notes) {
      if (note.folder === oldPath) {
        batch.update(getNoteDoc(vaultId, note.id), {
          folder: newPath,
          updatedAt: serverTimestamp()
        })
      } else if (note.folder.startsWith(oldPath + '/')) {
        const newFolder = newPath + note.folder.slice(oldPath.length)
        batch.update(getNoteDoc(vaultId, note.id), {
          folder: newFolder,
          updatedAt: serverTimestamp()
        })
      }
    }

    await batch.commit()
  }

  /**
   * Delete a folder (move notes to parent or root)
   * @param {string} vaultId - Vault ID
   * @param {string} folderPath - Folder to delete
   * @param {boolean} deleteNotes - If true, delete notes; if false, move to parent
   */
  static async deleteFolder(vaultId, folderPath, deleteNotes = false) {
    if (!vaultId || !folderPath) return

    const notes = await NoteService.listNotes(vaultId)
    const batch = writeBatch(db)

    // Get parent folder path
    const segments = folderPath.split('/').filter(Boolean)
    const parentPath = segments.slice(0, -1).join('/') || ''

    for (const note of notes) {
      if (note.folder === folderPath || note.folder.startsWith(folderPath + '/')) {
        if (deleteNotes) {
          batch.delete(getNoteDoc(vaultId, note.id))
        } else {
          // Move to parent folder
          const relativePath = note.folder.slice(folderPath.length)
          const newFolder = parentPath + relativePath
          batch.update(getNoteDoc(vaultId, note.id), {
            folder: newFolder.replace(/^\//, ''),
            updatedAt: serverTimestamp()
          })
        }
      }
    }

    await batch.commit()
  }

  // ============================================================================
  // SEARCH
  // ============================================================================

  /**
   * Search notes in a vault
   * @param {string} vaultId - Vault ID
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Matching notes
   */
  static async searchNotes(vaultId, searchQuery, options = {}) {
    const { searchContent = false, folder = null, limit = 50 } = options

    const notes = await NoteService.listNotes(vaultId)
    const q = (searchQuery || '').toLowerCase().trim()

    if (!q) return notes.slice(0, limit)

    const results = notes.filter((note) => {
      // Filter by folder if specified
      if (folder !== null && note.folder !== folder) return false

      // Search in title
      if (note.title.toLowerCase().includes(q)) return true

      // Search in tags
      if (note.tags?.some((t) => t.toLowerCase().includes(q))) return true

      // Search in aliases
      if (note.aliases?.some((a) => a.toLowerCase().includes(q))) return true

      // Search in content if enabled
      if (searchContent && note.content?.toLowerCase().includes(q)) return true

      return false
    })

    return results.slice(0, limit)
  }
}

export default NoteService
